"use client";

import React from "react";
import { NextIntlClientProvider } from "next-intl";
import type { AbstractIntlMessages } from "next-intl";
import { useSearchParams } from "next/navigation";
import { shouldShowI18nKeys } from "@/lib/i18n/shouldShowI18nKeys";
import { transformMessagesToKeys } from "@/lib/i18n/transformMessagesToKeys";
import { fetchTolgeeTranslationsCached } from "@/lib/tolgee/fetchTranslationsCached";
import { TOLGEE_REMOTE_ENABLED } from "@/lib/tolgee/config";
import {
  getStaticUiLocale,
  normalizeUiLocale,
  type StaticUiLocale,
} from "@/lib/i18n/uiLocale";
import { isSystems247Brand } from "@/lib/branding";
import { normalizeUiLanguageOptions, SYSTEMS247_UI_LANGUAGES } from "@/shared/i18n/languages";

export type RuntimeLocale = string;

const DEFAULT_TIME_ZONE = "Asia/Almaty";
const SYSTEMS247_SUPPORTED_LOCALES = new Set(
  normalizeUiLanguageOptions(SYSTEMS247_UI_LANGUAGES).map((option) => option.locale)
);

// === НОВЫЕ ТИПЫ ДЛЯ ОПТИМИЗАЦИИ ===

export type TolgeeLoadingStatus = 
  | 'idle'           // Не начинал загрузку
  | 'loading'        // Первая попытка
  | 'retrying'       // Повторная попытка
  | 'loaded'         // Успешно загружен
  | 'failed';        // Не удалось загрузить

interface LoadMessagesResult {
  static: AbstractIntlMessages;
  tolgee: AbstractIntlMessages;
  tolgeeSuccess: boolean;
}

// === КОНСТАНТЫ ОПТИМИЗАЦИИ ===

const TOLGEE_RACE_TIMEOUT_MS = 300;     // Race condition: ждём Tolgee максимум 300ms
const TOLGEE_MAX_WAIT_MS = 1000;        // Максимум 1 секунда на первую попытку
const TOLGEE_RETRY_COUNT = 2;           // 2 повторные попытки
const TOLGEE_RETRY_DELAYS = [1000, 2000]; // 1s, затем 2s между retry

type I18nRuntimeContextValue = {
  locale: RuntimeLocale;
  messages: AbstractIntlMessages;
  changeLanguage: (locale: RuntimeLocale) => Promise<void>;
  // === НОВЫЕ ПОЛЯ ДЛЯ МОНИТОРИНГА СТАТУСА ===
  tolgeeStatus: TolgeeLoadingStatus;
  tolgeeLoaded: boolean;
  retryCount: number;
};

const I18nRuntimeContext = React.createContext<I18nRuntimeContextValue | null>(null);

function normalizeLocale(input: string | null | undefined): RuntimeLocale {
  return normalizeUiLocale(input) ?? "en";
}

function normalizeLocaleForBrand(input: string | null | undefined): RuntimeLocale {
  const normalized = normalizeLocale(input);

  if (isSystems247Brand()) {
    return SYSTEMS247_SUPPORTED_LOCALES.has(normalized) ? normalized : "en";
  }

  return normalized;
}

function setLocaleCookies(locale: RuntimeLocale) {
  const maxAge = 60 * 60 * 24 * 365; // 1 year
  document.cookie = `locale=${locale}; path=/; max-age=${maxAge}; SameSite=Lax`;
  document.cookie = `NEXT_LOCALE=${locale}; path=/; max-age=${maxAge}; SameSite=Lax`;
}

function isPlainObject(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function deepMergeMessages(
  base: AbstractIntlMessages,
  override: AbstractIntlMessages,
): AbstractIntlMessages {
  const result: Record<string, unknown> = { ...base };

  for (const [key, overrideValue] of Object.entries(override)) {
    const baseValue = result[key];

    if (isPlainObject(baseValue) && isPlainObject(overrideValue)) {
      result[key] = deepMergeMessages(
        baseValue as AbstractIntlMessages,
        overrideValue as AbstractIntlMessages,
      );
      continue;
    }

    result[key] = overrideValue;
  }

  return result as AbstractIntlMessages;
}

async function loadStaticMessages(locale: RuntimeLocale): Promise<AbstractIntlMessages> {
  const ruMessages = (await import("@/i18n/ru.json")).default as AbstractIntlMessages;
  const staticLocale: StaticUiLocale = getStaticUiLocale(locale);

  if (staticLocale === "ru") return ruMessages;

  switch (staticLocale) {
    case "kk": {
      const target = (await import("@/i18n/kk.json")).default as AbstractIntlMessages;
      return deepMergeMessages(ruMessages, target);
    }
    case "en": {
      const target = (await import("@/i18n/en.json")).default as AbstractIntlMessages;
      return deepMergeMessages(ruMessages, target);
    }
    case "fr": {
      const target = (await import("@/i18n/fr.json")).default as AbstractIntlMessages;
      return deepMergeMessages(ruMessages, target);
    }
    case "fr-ca": {
      // fr-ca inherits from fr: missing keys fall back to fr, then ru.
      const fr = (await import("@/i18n/fr.json")).default as AbstractIntlMessages;
      const frCa = (await import("@/i18n/fr-ca.json")).default as AbstractIntlMessages;
      return deepMergeMessages(deepMergeMessages(ruMessages, fr), frCa);
    }
    case "es": {
      const target = (await import("@/i18n/es.json")).default as AbstractIntlMessages;
      return deepMergeMessages(ruMessages, target);
    }
    case "zh": {
      const target = (await import("@/i18n/zh.json")).default as AbstractIntlMessages;
      return deepMergeMessages(ruMessages, target);
    }
    default:
      return ruMessages;
  }
}

async function loadTolgeeMessages(
  locale: RuntimeLocale,
  isRetry: boolean = false
): Promise<AbstractIntlMessages> {
  if (!TOLGEE_REMOTE_ENABLED) return {};

  try {
    const tolgeeMessages = await fetchTolgeeTranslationsCached({ 
      language: locale,
      isRetry,
    });
    return tolgeeMessages;
  } catch (error) {
    const errorMsg = error instanceof Error ? error.message : 'Unknown error';
    console.error(`[I18n] Failed to load Tolgee (retry=${isRetry}):`, errorMsg);
    return {};
  }
}

/**
 * Загружает переводы параллельно с умным приоритетом Tolgee.
 * Использует Race condition для быстрого отображения.
 */
async function loadMessagesWithPriority(
  locale: RuntimeLocale,
  signal?: AbortSignal
): Promise<LoadMessagesResult> {
  // Запускаем оба запроса параллельно
  const staticPromise = loadStaticMessages(locale);
  
  // ⚠️ ВАЖНО: loadTolgeeMessages внутри использует fetchTolgeeTranslationsCached
  // Если Tolgee уже в кеше - вернётся мгновенно (БЕЗ сетевого запроса)
  const tolgeePromise = loadTolgeeMessages(locale, false);
  
  // Race condition: кто первый в пределах 300ms
  const raceResult = await Promise.race([
    tolgeePromise.then(data => ({ 
      source: 'tolgee' as const, 
      data,
      success: Object.keys(data).length > 0 
    })),
    staticPromise.then(() => ({ 
      source: 'static' as const, 
      data: null,
      success: false  
    })),
    // Timeout fallback - гарантия что через 300ms покажем хоть что-то
    new Promise<{ source: 'timeout'; data: null; success: false }>(resolve =>
      setTimeout(() => resolve({ 
        source: 'timeout', 
        data: null,
        success: false 
      }), TOLGEE_RACE_TIMEOUT_MS)
    ),
  ]);
  
  // Проверка на отмену
  if (signal?.aborted) {
    throw new Error('Aborted');
  }
  
  // Ждём завершения обоих (чтобы иметь fallback)
  const [tolgeeMessages, staticMessages] = await Promise.all([
    tolgeePromise.catch((err) => {
      console.error('[I18n] Tolgee load failed:', err);
      return {};
    }),
    staticPromise,
  ]);
  
  const tolgeeSuccess = Object.keys(tolgeeMessages).length > 0;
  
  return {
    static: staticMessages,
    tolgee: tolgeeMessages,
    tolgeeSuccess,
  };
}

/**
 * Загружает Tolgee с retry механизмом для надёжности
 */
async function loadTolgeeWithRetry(
  locale: RuntimeLocale,
  onRetry?: (attempt: number) => void,
  signal?: AbortSignal
): Promise<AbstractIntlMessages> {
  if (!TOLGEE_REMOTE_ENABLED) return {};

  for (let attempt = 0; attempt <= TOLGEE_RETRY_COUNT; attempt++) {
    try {
      if (signal?.aborted) {
        return {};
      }
      
      if (attempt > 0) {
        onRetry?.(attempt);
        // Экспоненциальная задержка
        const delay = TOLGEE_RETRY_DELAYS[attempt - 1];
        await new Promise(resolve => setTimeout(resolve, delay));
      }
      
      const messages = await loadTolgeeMessages(locale, attempt > 0);
      
      if (Object.keys(messages).length > 0) {
        return messages;
      }
      
      // Пустой ответ - считается как ошибка для retry
      if (attempt < TOLGEE_RETRY_COUNT) {
        console.warn(`[Tolgee] Empty response, retrying... (${attempt + 1}/${TOLGEE_RETRY_COUNT})`);
      }
      
    } catch (error) {
      if (attempt === TOLGEE_RETRY_COUNT) {
        console.error(`[Tolgee] Failed after ${TOLGEE_RETRY_COUNT + 1} attempts:`, error);
        return {};
      }
      
      console.warn(`[Tolgee] Attempt ${attempt + 1} failed, retrying...`);
    }
  }
  
  return {};
}

export function useRuntimeLocale(): I18nRuntimeContextValue {
  const ctx = React.useContext(I18nRuntimeContext);
  if (!ctx) {
    throw new Error("useRuntimeLocale must be used within I18nRuntimeProvider");
  }
  return ctx;
}

type I18nRuntimeProviderProps = {
  children: React.ReactNode;
  initialLocale: string;
  initialMessages: AbstractIntlMessages;
  showKeysFromServer: boolean;
};

function I18nRuntimeProviderInner({
  children,
  initialLocale,
  initialMessages,
  showKeysFromServer,
}: I18nRuntimeProviderProps) {
  const [locale, setLocale] = React.useState<RuntimeLocale>(() =>
    normalizeLocaleForBrand(initialLocale)
  );
  const [messages, setMessages] = React.useState<AbstractIntlMessages>(initialMessages);
  const [tolgeeLoaded, setTolgeeLoaded] = React.useState(false);
  
  // === НОВЫЕ STATES ДЛЯ МОНИТОРИНГА СТАТУСА ===
  const [tolgeeStatus, setTolgeeStatus] = React.useState<TolgeeLoadingStatus>('idle');
  const [retryCount, setRetryCount] = React.useState(0);

  const searchParams = useSearchParams();
  const showKeysClient = shouldShowI18nKeys(searchParams);
  const [isFirstRender, setIsFirstRender] = React.useState(true);

  React.useEffect(() => {
    setIsFirstRender(false);
  }, []);

  const showKeys = isFirstRender ? showKeysFromServer : showKeysClient;

  const changeSeqRef = React.useRef(0);

  React.useEffect(() => {
    let cancelled = false;
    const abortController = new AbortController();

    async function loadTranslations() {
      if (!TOLGEE_REMOTE_ENABLED) {
        const staticMessages = await loadStaticMessages(locale);
        if (cancelled) return;
        setMessages(staticMessages);
        setTolgeeLoaded(false);
        setTolgeeStatus('idle');
        return;
      }

      setTolgeeStatus('loading');
      
      try {
        // === ЭТАП 1: Параллельная загрузка с приоритетом Tolgee ===
        const result = await loadMessagesWithPriority(locale, abortController.signal);
        
        if (cancelled) return;
        
        // === ЭТАП 2: Применение результата ===
        
        if (result.tolgeeSuccess) {
          // ✅ Tolgee загрузился быстро (< 300ms или из кеша) - показываем Tolgee сразу
          setMessages(deepMergeMessages(result.static, result.tolgee));
          setTolgeeLoaded(true);
          setTolgeeStatus('loaded');
          
          console.log('[I18n] Tolgee loaded successfully (fast)');
          
        } else {
          // ⏳ Tolgee не успел или упал - показываем локальные, но продолжаем ждать
          setMessages(result.static);
          setTolgeeLoaded(false);
          setTolgeeStatus('loading');
          
          console.log('[I18n] Using static files, retrying Tolgee in background...');
          
          // === ЭТАП 3: Retry Tolgee в фоне ===
          const tolgeeRetryMessages = await loadTolgeeWithRetry(
            locale,
            (attempt) => {
              if (!cancelled) {
                setRetryCount(attempt);
                setTolgeeStatus('retrying');
              }
            },
            abortController.signal
          );
          
          if (cancelled) return;
          
          // === ЭТАП 4: Обновление UI если retry успешен ===
          
          if (Object.keys(tolgeeRetryMessages).length > 0) {
            // ✅ Retry успешен - плавно обновляем на Tolgee (ПРИОРИТЕТ!)
            setMessages(deepMergeMessages(result.static, tolgeeRetryMessages));
            setTolgeeLoaded(true);
            setTolgeeStatus('loaded');
            setRetryCount(0);
            
            console.log('[I18n] Tolgee loaded successfully (after retry)');
            
          } else {
            // ❌ Все попытки неудачны - остаёмся на локальных файлах
            setTolgeeStatus('failed');
            setRetryCount(0);
            
            console.warn('[I18n] Tolgee failed after retries, using static fallback');
          }
        }
        
      } catch (error) {
        if (!cancelled) {
          console.error('[I18n] Load error:', error);
          
          // Fallback на локальные файлы при критической ошибке
          const staticMessages = await loadStaticMessages(locale);
          setMessages(staticMessages);
          setTolgeeLoaded(false);
          setTolgeeStatus('failed');
          setRetryCount(0);
        }
      }
    }

    loadTranslations();

    return () => {
      cancelled = true;
      abortController.abort();
    };
  }, [locale]);

  const changeLanguage = React.useCallback(async (nextLocale: RuntimeLocale) => {
    const normalized = normalizeLocaleForBrand(nextLocale);
    setLocaleCookies(normalized);

    const seq = ++changeSeqRef.current;

    setLocale(normalized);
    setTolgeeStatus('loading');
    setRetryCount(0);

    try {
      // === Параллельная загрузка для быстрой смены языка ===
      const [staticMessages, tolgeeMessages] = await Promise.all([
        loadStaticMessages(normalized),
        loadTolgeeWithRetry(
          normalized,
          (attempt) => {
            if (seq === changeSeqRef.current) {
              setRetryCount(attempt);
              setTolgeeStatus('retrying');
            }
          }
        ),
      ]);

      // Защита от race condition при быстрой смене языков
      if (seq !== changeSeqRef.current) return;

      // Применяем результат с приоритетом Tolgee
      if (Object.keys(tolgeeMessages).length > 0) {
        // ✅ Tolgee загрузился - показываем Tolgee (ПРИОРИТЕТ!)
        setMessages(deepMergeMessages(staticMessages, tolgeeMessages));
        setTolgeeLoaded(true);
        setTolgeeStatus('loaded');
        
        console.log(`[I18n] Language changed to ${normalized} (Tolgee)`);
        
      } else {
        // ❌ Tolgee не загрузился - показываем локальные
        setMessages(staticMessages);
        setTolgeeLoaded(false);
        setTolgeeStatus('failed');
        
        console.log(`[I18n] Language changed to ${normalized} (Static fallback)`);
      }
      
      setRetryCount(0);
      
    } catch (error) {
      if (seq !== changeSeqRef.current) return;
      
      console.error('[I18n] Language change error:', error);
      
      // Критическая ошибка - используем локальные
      const staticMessages = await loadStaticMessages(normalized);
      setMessages(staticMessages);
      setTolgeeLoaded(false);
      setTolgeeStatus('failed');
      setRetryCount(0);
    }
  }, []);

  const displayMessages = React.useMemo(() => {
    return showKeys ? transformMessagesToKeys(messages) : messages;
  }, [messages, showKeys]);

  const value = React.useMemo<I18nRuntimeContextValue>(
    () => ({
      locale,
      messages: displayMessages,
      changeLanguage,
      // === НОВЫЕ ПОЛЯ ДЛЯ МОНИТОРИНГА ===
      tolgeeStatus,
      tolgeeLoaded,
      retryCount,
    }),
    [locale, displayMessages, changeLanguage, tolgeeStatus, tolgeeLoaded, retryCount]
  );

  return (
    <I18nRuntimeContext.Provider value={value}>
      <NextIntlClientProvider
        locale={locale}
        messages={displayMessages}
        timeZone={DEFAULT_TIME_ZONE}
      >
        {children}
      </NextIntlClientProvider>
    </I18nRuntimeContext.Provider>
  );
}

export function I18nRuntimeProvider(props: I18nRuntimeProviderProps) {
  return (
    <I18nRuntimeProviderInner {...props} />
  );
}
